import { autobind } from "core-decorators";

import { Inject } from "typescript-ioc";
import { UiComponentFactory } from "Ui/Scripts/UiComponentFactory";
import { EventBinder } from "Events/Scripts/EventBinder";
import { LoggerFactory } from "Logging/Scripts/LoggerFactory";
import { FetchService } from "Async/Scripts/FetchService";
import { PickupPoint } from "./PickupPoint";
import { ShippingOffer } from "./ShippingOffer/ShippingOffer";
import { Utils } from "Helpers/Scripts/Utils";

import { IPickupPointFiltersData } from "./IPickupPointFiltersData";
import { IPickupPointFetch } from "./IPickupPointFetch";
import { IPickupPointModel } from "./IPickupPointModel";

export class PickupPointWithoutMap extends PickupPoint {
    private _selectedDeliveryChannel: any = null; // TODO: This is not reassigned to any other value then null - maybe should be removed

    private _noPickPointsFoundElement: Element | null;
    private _isVisibleNoPickPointsFound: boolean | null;
    private _previousSearchTerm: string;
    private _lastFilterAction: string; // can be "ACTION_SELECT" or "ACTION_ENTER"
    private readonly ACTION_SELECT: string = "actionSelect";
    private readonly ACTION_ENTER: string = "actionEnter";
    private _oldAutoCompleteValue: string = ""; // used for mobile fix in Chinese laguage
    private _intervalTime: number = 200; // used for mobile fix in Chinese laguage
    public get key(): string {
        return "PickupPointWithoutMap";
    }

    constructor(
        @Inject componentFactory: UiComponentFactory,
        @Inject binder: EventBinder,
        @Inject loggerFactory: LoggerFactory,
        @Inject fetchService: FetchService,
        @Inject shippingOfferComponent: ShippingOffer
    ) {
        super(componentFactory, binder, loggerFactory, fetchService, shippingOfferComponent);
    }

    public init(): void {
        this._noPickPointsFoundElement = this.findElement(".js-pickup-point-no-rows-available");

        this._setUrlLinks();

        this._setFilters();
        this._bindEvents();
        super._initSelectedPickupPoint();
        this._bindAutoCompleteRefreshFix(); // mobile fix in Chinese laguage
    }

    private _setFilters(): void {
        if (this._model) {
            this._setFilterType(this._model.DeliveryMethods, this._handleFilterTypeChange);
        }
        this._setFilterSearch(this._onFilterSearchChange);
    }

    /* eslint-disable @typescript-eslint/promise-function-async */
    private _loadPickupPoints(ajaxUrl?: string): Promise<IPickupPointFetch> {
        const data = new FormData();
        for (const key of Object.keys(this._filtersCurrentData)) {
            data.append(
                key,
                this._filtersCurrentData[key as keyof IPickupPointFiltersData] as string
            );
        }
        if (ajaxUrl) {
            return this._fetchService.post<IPickupPointFetch>(ajaxUrl, data);
        } else {
            return this._fetchService.post<IPickupPointFetch>(this._getPickupPointsUrl, data);
        }
    }
    /* eslint-enable @typescript-eslint/promise-function-async */

    @autobind
    private _handleFilterTypeChange(event: kendo.ui.DropDownListChangeEvent): void {
        this._updateFiltersCurrentData(event.sender.value(), this.FILTER_DELIVERY_METHOD);
        this._fetchDeliveryChannelModels();
        this._loadAndShowPickupPoints();
    }

    private _onFilterSearchChange(event: kendo.ui.AutoCompleteEvent): void {
        this._handleFilterSearchChange(
            event,
            () => this._loadAndShowPickupPoints(),
            () => {
                this._selectedDeliveryChannel = null;
            },
            () => this._loadAndShowPickupPoints()
        );
    }

    private _loadAndShowPickupPoints(ajaxUrl?: string): void {
        this._loadPickupPoints(ajaxUrl).then((data: IPickupPointFetch) => {
            if (this._noPickPointsFoundElement) {
                this._isVisibleNoPickPointsFound = Utils.isVisible(this._noPickPointsFoundElement);
                if (!data || data.PickupPoints.length === 0) {
                    this._logger.warning("No pickup points found.");

                    const pickupPointResults = this.findElement(this.PICKUP_POINTS_RESULTS);
                    if (pickupPointResults && pickupPointResults.children.length > 0) {
                        this._hideAndRemoveResults();
                    }
                    this._noPickPointsFoundElement.removeAttribute("style");
                    Utils.removeClass(this._noPickPointsFoundElement, this.CLASS_HIDDEN);

                    return;
                }

                this._channelState = data.PickupPoints;
                this.hideNoPickPointsFoundElement();
                Utils.addClass(this._noPickPointsFoundElement, this.CLASS_HIDDEN);
                this._swapShownChannels(data.IsNextPageAvailable);
            }
        }, this._displayError);
    }

    public hideNoPickPointsFoundElement(): void {
        this._isVisibleNoPickPointsFound =
            this._noPickPointsFoundElement && Utils.isVisible(this._noPickPointsFoundElement);
        if (this._isVisibleNoPickPointsFound && this._noPickPointsFoundElement) {
            Utils.slideUp(this._noPickPointsFoundElement, this.SLIDE_SPEED);
        }
    }

    private _bindEvents(): void {
        this.bindClick(this._handleClick);
        this._bindSinglePickpuPointSelect();
        this._bindLoadMorePointsEvent();
        this._bindSubmitAction();
        this._bindForceFilterSearch();
    }

    @autobind
    private _bindSubmitAction(): void {
        this._binder.bindKeyDown(
            (e) => this._submitAction(e as JQuery.TriggeredEvent),
            this._filters.search.selector
        );
    }

    // TODO: remove un-D.R.Y. approach
    private _submitAction(event: JQuery.TriggeredEvent): void {
        if (
            (event.keyCode === this.ENTER_KEY_CODE ||
                Utils.hasClass(
                    <HTMLSpanElement>event.target,
                    this.PICKUP_POINTS_SEARCH_BUTTON_LABEL
                ) ||
                (<HTMLInputElement>event.target).name === this.PICKUP_POINTS_SEARCH_BUTTON) &&
            this._filtersCurrentData.searchTerm !== "" &&
            this._filtersCurrentData.searchTerm !== this._previousSearchTerm &&
            !this._isOptionHighlighted()
        ) {
            this._filtersCurrentData.requestId++;
            this._filtersCurrentData.page = 0;
            this._fetchDeliveryChannelModels();
        }
        this._previousSearchTerm = this._filtersCurrentData.searchTerm;
    }

    private _fetchDeliveryChannelModels(): void {
        this._loadAndShowPickupPoints(this._getDeliveryChannelModels);
        this._setLastFilterAction(this.ACTION_ENTER);
    }

    private _setLastFilterAction(action: string): void {
        this._lastFilterAction = action;
    }

    @autobind
    private _handleClick(event: JQuery.TriggeredEvent): void {
        const value = Number(Utils.getValue(event.target));
        if (this._isClickOnPickupChannel(event) && this._selectedDeliveryChannel !== value) {
            this._sendChannelUpdate(value);
        }
        if (this._isClickOnSearchButton(event)) {
            this._submitAction(event);
        }
    }

    private _bindSinglePickpuPointSelect(): void {
        const textSearch = kendo.getAutoComplete(this._filters.search.selector);
        if (textSearch) {
            textSearch.bindSelect(this._handleSinglePickupPointSelection);
        }
    }

    @autobind
    private _handleSinglePickupPointSelection(event: kendo.ui.AutoCompleteChangeEvent): void {
        if (event) {
            this._setLastFilterAction(this.ACTION_SELECT);
            this._filtersCurrentData.page = 0;
        }
    }

    private _bindLoadMorePointsEvent(): void {
        this._binder.bindDelegatedClick(this.LOAD_MORE_BUTTON, () => {
            this._filtersCurrentData.page += 1;
            let actionUrl: string;
            if (this._lastFilterAction === this.ACTION_ENTER) {
                actionUrl = this._getDeliveryChannelModels;
            } else {
                actionUrl = this._getPickupPointsUrl;
            }
            this._loadPickupPoints(actionUrl).then((data: IPickupPointFetch) => {
                if (!data || data.PickupPoints.length === 0) {
                    this._showMoreButtonToggle(true);
                    return;
                }
                this._appendResults(data.PickupPoints);

                if (!this._channelState) {
                    this._channelState = [];
                }

                this._channelState.push(...data.PickupPoints);
                if (!data.IsNextPageAvailable) {
                    this._filtersCurrentData.page = 0;
                    this._showMoreButtonToggle(true);
                }
            }, this._displayError);
        });
    }

    public update(_: any, pageNumber?: number): void {
        let actionUrl: string;
        const renderedPickupPoints = this._getRenderedPickupPoints();
        if (renderedPickupPoints) {
            this._filtersCurrentData.pageSize = renderedPickupPoints;
        }
        if (this._lastFilterAction === this.ACTION_ENTER) {
            actionUrl = this._getDeliveryChannelModels;
            if (pageNumber) {
                this._filtersCurrentData.page = pageNumber;
            }
        } else {
            actionUrl = this._getPickupPointsUrl;
        }
        this._loadPickupPoints(actionUrl).then((data: IPickupPointFetch) => {
            if (!data) {
                this._logger.info("No pickup points found.");
                return;
            }
            data.PickupPoints.forEach((item: IPickupPointModel, index: number) => {
                if (Array.isArray(this._channelState) && this._channelState.length > index) {
                    this._updateFromDeliveryModel(this._channelState[index], item, true);

                    const isOutOfStockChanged = this._wasOutOfStockItemsChanged(
                        this._channelState[index].OutOfStockItems,
                        item.OutOfStockItems
                    );
                    const isEndCustomerAddressChanged =
                        this._isEndCustomerFirstCheckout() &&
                        this._isChanged(this._channelState[index].Address, item.Address);
                    const isDeliveryDateChanged = this._isChanged(
                        this._channelState[index].DeliveryDate,
                        item.DeliveryDate
                    );

                    if (
                        isOutOfStockChanged ||
                        isDeliveryDateChanged ||
                        isEndCustomerAddressChanged
                    ) {
                        this._updateAddressInfo(item);
                    }
                }
            });
            this._channelState = data.PickupPoints;
        }, this._displayError);
        this._filtersCurrentData.pageSize = null;
    }

    private _getRenderedPickupPoints(): number {
        const renderedPickupPoints = this.find("[name='deliveryChannelPickupPoint']");
        return renderedPickupPoints.length;
    }

    private _bindForceFilterSearch(): void {
        this.bindDelegatedClick(this._filters.search.selector, this._forceFilterSearch);
    }

    @autobind
    private _forceFilterSearch(): void {
        const kendoAutoComplete = kendo.getAutoComplete(this._filters.search.selector);
        if (kendoAutoComplete) {
            const value = kendoAutoComplete.value();
            if (kendoAutoComplete.value() !== "") {
                kendoAutoComplete.search(value);
            }
        }
    }

    // START: Safari + iOS mobile fix (for Chinese)
    private _bindAutoCompleteRefreshFix(): void {
        let isMobileFirefoxOrIos = false;
        if (ori.shopping && ori.shopping.wizard) {
            isMobileFirefoxOrIos =
                ori.shopping.wizard.isMobileFirefox() || ori.shopping.wizard.isMobileIos();
            if (isMobileFirefoxOrIos) {
                this.bindClick(this._setAutoFiltering, this._filters.search.selector);
            }
        }
    }

    @autobind
    private _isAutoCompleteChanged(): void {
        const kendoAutoComplete = kendo.getAutoComplete(this._filters.search.selector);
        if (kendoAutoComplete) {
            const newAutoCompleteValue = kendoAutoComplete.value();
            if (
                kendoAutoComplete &&
                newAutoCompleteValue !== this._oldAutoCompleteValue &&
                newAutoCompleteValue !== ""
            ) {
                this._oldAutoCompleteValue = newAutoCompleteValue;
                kendoAutoComplete.search(newAutoCompleteValue);
            }
        }
    }

    @autobind
    private _setAutoFiltering(): void {
        const kendoAutoComplete = kendo.getAutoComplete(this._filters.search.selector);
        if (kendoAutoComplete && !this._intervalId) {
            this._intervalId = window.setInterval(this._isAutoCompleteChanged, this._intervalTime);
        }
    }
    // END: Safari + iOS mobile fix (for Chinese)
}
